import React from 'react';
import { FaQrcode, FaEdit, FaEye, FaUserCheck, FaTrash } from 'react-icons/fa';

const ClubProjectsTable = ({ projects, openModal, handleDeleteClick }) => {
    return (
        <div className="w-full">
            {/* Desktop Table View */}
            <div className="hidden md:block">
                <table className="w-full">
                    <thead>
                        <tr className="bg-gray-50/50 border-b border-gray-100">
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Project Name</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Attendance</th>
                            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {projects.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                    No projects available
                                </td>
                            </tr>
                        ) : (
                            projects.map((project) => (
                                <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{project.name}</td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-medium">
                                            {project.project_category_name || 'General'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm">
                                        {Number(project.is_open) === 1 ? (
                                            <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-2.5 py-1 rounded-full text-xs font-bold ring-1 ring-inset ring-green-600/20">
                                                <span className="w-1.5 h-1.5 rounded-full bg-green-600 animate-pulse"></span>
                                                Open
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 px-2.5 py-1 rounded-full text-xs font-bold ring-1 ring-inset ring-red-600/20">
                                                <span className="w-1.5 h-1.5 rounded-full bg-red-600"></span>
                                                Closed
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600 font-bold">
                                        {project.attendance_count} Attendees
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-3">
                                            <button
                                                onClick={() => openModal('view-qr', project)}
                                                className="text-purple-600 hover:text-purple-800 text-sm font-medium transition-colors flex items-center gap-1"
                                                title="Show QR Code"
                                            >
                                                <FaQrcode />
                                            </button>
                                            <button
                                                onClick={() => openModal('edit-project', project)}
                                                className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                                                title="Edit Project"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                onClick={() => openModal('view-attendance', project)}
                                                className="text-green-600 hover:text-green-800 text-sm font-medium transition-colors"
                                                title="View Attendance"
                                            >
                                                <FaEye />
                                            </button>
                                            <button
                                                onClick={() => openModal('mark-attendance', { project_id: project.id, fixedProject: true })}
                                                className="text-indigo-600 hover:text-indigo-800 text-sm font-medium transition-colors"
                                                title="Mark Attendance"
                                            >
                                                <FaUserCheck />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(project.id)}
                                                className="text-red-500 hover:text-red-700 text-sm font-medium transition-colors"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile Grid View */}
            <div className="md:hidden divide-y divide-gray-100">
                {projects.length === 0 ? (
                    <div className="px-6 py-8 text-center text-gray-500">
                        No projects available
                    </div>
                ) : (
                    projects.map((project) => (
                        <div key={project.id} className="p-4 space-y-4">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="font-bold text-gray-900 text-lg">{project.name}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                                            {project.project_category_name || 'General'}
                                        </span>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase">
                                            {new Date(project.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-3 py-1 rounded-lg text-center">
                                    <div className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">Attendees</div>
                                    <div className="text-sm font-black text-gray-900">{project.attendance_count}</div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-50">
                                <div className="flex gap-3">
                                    <button onClick={() => openModal('view-qr', project)} className="p-2 bg-purple-50 text-purple-600 rounded-lg"><FaQrcode /></button>
                                    <button onClick={() => openModal('edit-project', project)} className="p-2 bg-blue-50 text-blue-600 rounded-lg"><FaEdit /></button>
                                    <button onClick={() => openModal('view-attendance', project)} className="p-2 bg-green-50 text-green-600 rounded-lg"><FaEye /></button>
                                    <button onClick={() => openModal('mark-attendance', { project_id: project.id, fixedProject: true })} className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><FaUserCheck /></button>
                                </div>
                                <button onClick={() => handleDeleteClick(project.id)} className="p-2 bg-red-50 text-red-500 rounded-lg"><FaTrash /></button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ClubProjectsTable;
